/**
 * Kakara Theme - Global JavaScript
 * Inspired by NZ Fragrance Market Aesthetics
 */

(function() {
  'use strict';

  /**
   * Fixed marquee + nav chrome heights for all templates.
   * Homepage home-scroll.js extends these with drawer/hero vars.
   */
  const siteChrome = {
    sync() {
      const ann = document.querySelector('.section-announcement-bar')
        || document.querySelector('.announcement-bar');
      const headerEl = document.querySelector('.header');
      const annH = ann ? Math.ceil(ann.getBoundingClientRect().height) : 0;
      const headerH = headerEl ? Math.round(headerEl.getBoundingClientRect().height) : 48;
      document.documentElement.style.setProperty('--hero-announcement-offset', `${annH}px`);
      document.documentElement.style.setProperty('--header-h', `${headerH}px`);
    },

    init() {
      this.sync();
      window.addEventListener('resize', () => this.sync(), { passive: true });
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.sync());
      }
    },
  };

  // Header functionality
  const header = {
    init() {
      this.bindEvents();
    },

    bindEvents() {
      // Mobile menu toggle
      const menuToggle = document.querySelector('[data-menu-toggle]');
      const mobileNav = document.querySelector('[data-mobile-nav]');
      const mobileNavClose = document.querySelector('[data-mobile-nav-close]');
      const headerOverlay = document.querySelector('[data-header-overlay]');

      if (menuToggle && mobileNav) {
        menuToggle.addEventListener('click', () => {
          mobileNav.classList.add('is-active');
          headerOverlay?.classList.add('is-active');
          document.body.style.overflow = 'hidden';
        });

        mobileNavClose?.addEventListener('click', () => {
          this.closeMobileNav(mobileNav, headerOverlay);
        });

        headerOverlay?.addEventListener('click', () => {
          this.closeMobileNav(mobileNav, headerOverlay);
        });

        /* Same-page collection hashes (Koru → Car Fragrance) do not reload,
           so the open drawer would hide the tab change. */
        mobileNav.addEventListener('click', (event) => {
          const link = event.target.closest('a[href]');
          if (!link || !mobileNav.contains(link)) return;
          if (event.button !== 0) return;
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          this.closeMobileNav(mobileNav, headerOverlay);
        });
      }

      // Search toggle (mobile button left of burger, desktop in icons)
      const searchToggles = document.querySelectorAll('[data-search-toggle]');
      const searchDrawer = document.querySelector('[data-search-drawer]');
      const searchClose = document.querySelector('[data-search-close]');

      if (searchToggles.length && searchDrawer) {
        searchToggles.forEach((toggle) => {
          toggle.addEventListener('click', () => {
            searchDrawer.classList.add('is-active');
            searchDrawer.querySelector('input')?.focus();
          });
        });

        searchClose?.addEventListener('click', () => {
          searchDrawer.classList.remove('is-active');
        });
      }
    },

    closeMobileNav(mobileNav, overlay) {
      mobileNav.classList.remove('is-active');
      overlay?.classList.remove('is-active');
      document.body.style.overflow = '';
    }
  };

  // Hero parallax – layered scroll effect
  const heroParallax = {
    ticking: false,
    hero: null,
    heroHeight: 0,
    layers: null,

    init() {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) return;

      // The logo-reveal scene replaces the parallax behaviour on the homepage.
      if (document.querySelector('[data-hero-reveal]')) return;

      this.hero = document.querySelector('.section-hero .hero');
      if (!this.hero) return;

      this.cacheLayers();
      window.addEventListener('scroll', () => this.requestTick(), { passive: true });
      window.addEventListener('resize', () => this.cacheLayers(), { passive: true });
      this.requestTick();
    },

    cacheLayers() {
      const hero = this.hero;
      if (!hero) return;
      this.heroHeight = hero.offsetHeight;
      this.layers = {
        image: hero.querySelector('.hero__image'),
        overlay: hero.querySelector('.hero__overlay'),
        subheading: hero.querySelector('.hero__subheading'),
        heading: hero.querySelector('.hero__heading'),
        actions: hero.querySelector('.hero__actions'),
      };
    },

    requestTick() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => this.update());
    },

    update() {
      this.ticking = false;
      const hero = this.hero;
      const layers = this.layers;
      if (!hero || !layers) return;

      const rect = hero.getBoundingClientRect();
      const heroHeight = this.heroHeight || hero.offsetHeight;
      const viewHeight = window.innerHeight;

      // Only apply parallax while hero is in or near viewport
      if (rect.bottom < -100 || rect.top > viewHeight + 100) return;

      const scrollY = window.scrollY || window.pageYOffset;
      const maxOffset = heroHeight * 0.4;
      const scrollProgress = Math.min(scrollY / heroHeight, 1);
      const offset = scrollProgress * maxOffset;

      // Background lags most (positive Y = pulls down); foreground lags least
      const bgY = offset * 0.5;
      const subY = offset * 0.38;
      const headY = offset * 0.26;
      const actionY = offset * 0.12;

      if (layers.image) layers.image.style.transform = `scale(1.08) translate3d(0, ${bgY}px, 0)`;
      if (layers.overlay) layers.overlay.style.transform = `translate3d(0, ${bgY}px, 0)`;
      if (layers.subheading) layers.subheading.style.transform = `translate3d(0, ${subY}px, 0)`;
      if (layers.heading) layers.heading.style.transform = `translate3d(0, ${headY}px, 0)`;
      if (layers.actions) layers.actions.style.transform = `translate3d(0, ${actionY}px, 0)`;
    }
  };

  // Hero “Scent of …” rotating subheading (type → hold → backspace → next)
  const heroRotator = {
    init() {
      document.querySelectorAll('[data-hero-rotator]').forEach((root) => {
        const raw = root.getAttribute('data-rotator-endings') || '';
        const endings = raw.split('|').map((s) => s.trim()).filter(Boolean);
        if (endings.length < 2) return;

        const endingEl = root.querySelector('.hero__subheading-ending');
        if (!endingEl) return;

        const announceEl = root.querySelector('.hero__subheading-rotator-announce');
        const prefixEl = root.querySelector('.hero__subheading-prefix');
        const prefixDisplay = prefixEl ? prefixEl.textContent.trim() : '';

        const holdMs = Math.max(
          400,
          parseInt(root.getAttribute('data-rotator-interval') || '2800', 10) || 2800
        );
        const typeMs = 52;
        const deleteMs = 38;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        /** Keeps a line box when visually blank — avoids baseline jump vs truly empty nodes */
        const ZWSP = '\u200b';

        function announcePhrase(ending) {
          if (!announceEl) return;
          const suffix = ending.trim();
          announceEl.textContent = suffix ? `${prefixDisplay} ${suffix}` : prefixDisplay;
        }

        function delay(ms) {
          return new Promise((resolve) => window.setTimeout(resolve, ms));
        }

        function getEndingPlain() {
          return endingEl.textContent.replace(/\u200b/g, '').trim();
        }

        function setEndingText(text) {
          endingEl.textContent = text === '' ? ZWSP : text;
        }

        let idx = endings.indexOf(getEndingPlain());
        if (idx < 0) idx = 0;

        async function loop() {
          while (true) {
            const phrase = endings[idx];

            if (prefersReducedMotion) {
              setEndingText(phrase);
              announcePhrase(phrase);
              await delay(holdMs);
              idx = (idx + 1) % endings.length;
              continue;
            }

            // Plan order: type out → hold → backspace to empty → next phrase
            setEndingText('');
            let built = '';
            for (const ch of phrase) {
              built += ch;
              setEndingText(built);
              await delay(typeMs);
            }
            announcePhrase(phrase);

            await delay(holdMs);

            let current = phrase;
            while (current.length > 0) {
              current = [...current].slice(0, -1).join('');
              setEndingText(current);
              await delay(deleteMs);
            }

            idx = (idx + 1) % endings.length;
          }
        }

        loop();
      });
    }
  };

  // Phones and other pages fade with a view timeline in CSS.
  // The desktop homepage drawer is transformed, so that timeline never
  // finishes there. Those pieces fade from what is actually on screen.
  const scrollReveal = {
    init() {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) return;
      const viewOk = window.CSS && CSS.supports('animation-timeline: view()');
      if (!viewOk) this.revealSections();
      this.revealDesktopDrawer();
    },

    revealSections() {
      const sections = document.querySelectorAll(
        '.main-content .shopify-section:not(.section-hero), .footer__brand, .footer__menu'
      );
      if (!sections.length || typeof IntersectionObserver === 'undefined') return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

      sections.forEach(section => observer.observe(section));
    },

    revealDesktopDrawer() {
      if (!document.body.classList.contains('template-index')) return;
      if (!window.matchMedia('(min-width: 768px)').matches) return;
      if (typeof IntersectionObserver === 'undefined') return;

      const pieces = document.querySelectorAll([
        '.home-drawer .rich-text__heading',
        '.home-drawer .rich-text__lemma',
        '.home-drawer .rich-text__text',
        '.home-drawer .rich-text__button',
        '.home-drawer .promo-cards__subheading',
        '.home-drawer .promo-cards__heading',
        '.home-drawer .promo-cards__button',
        '.home-drawer .promo-cards__image',
        '.home-drawer .image-with-text__heading',
        '.home-drawer .image-with-text__text',
        '.home-drawer .image-with-text__button',
        '.home-drawer .image-with-text__image',
        '.home-drawer .featured-collection__heading',
        '.home-drawer .testimonials__heading',
        '.home-drawer .testimonials__card',
        '.home-drawer .newsletter__heading',
        '.home-drawer .newsletter__text'
      ].join(','));
      if (!pieces.length) return;

      const viewH = window.innerHeight || 1;
      const pending = [];
      const counts = new Map();
      pieces.forEach((el) => {
        const parent = el.parentElement;
        const n = counts.get(parent) || 0;
        counts.set(parent, n + 1);
        el.style.setProperty('--reveal-i', String(Math.min(n, 4)));
        const rect = el.getBoundingClientRect();
        const seen = rect.top < viewH * 0.92 && rect.bottom > 40;
        el.setAttribute('data-drawer-reveal', '');
        if (seen) el.classList.add('is-visible');
        else pending.push(el);
      });
      if (!pending.length) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });

      pending.forEach((el) => observer.observe(el));
    }
  };

  // Initialize
  document.addEventListener('DOMContentLoaded', () => {
    siteChrome.init();
    window.KakaraHomeScroll?.syncChrome?.();
    header.init();
    heroRotator.init();
    window.KakaraHomeScroll?.init();
    heroParallax.init();
    scrollReveal.init();
  });

  // Quantity inputs
  /* Product-page quantity steppers only — cart page/drawer own their qty UI. */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-form], [data-cart-drawer]')) return;

    const minusBtn = e.target.closest('[data-quantity-minus]');
    const plusBtn = e.target.closest('[data-quantity-plus]');

    if (minusBtn) {
      const input = minusBtn.parentElement.querySelector('[data-quantity-input]');
      if (input && parseInt(input.value, 10) > 1) {
        input.value = String(parseInt(input.value, 10) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (plusBtn) {
      const input = plusBtn.parentElement.querySelector('[data-quantity-input]');
      if (input) {
        input.value = String(parseInt(input.value, 10) + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  function applyProductCardVariant(card, { id, available, price, compare }) {
    if (!card) return;

    const form = card.querySelector('[data-product-card-form]');
    const variantInput = card.querySelector('[data-product-card-variant-id]');
    const priceEl = card.querySelector('[data-product-card-price]');
    const submitBtn = card.querySelector('[data-product-card-submit]');

    if (variantInput && id) variantInput.value = id;

    if (priceEl && price) {
      if (compare) {
        priceEl.innerHTML = `<div class="price price--on-sale"><span class="price__regular"><s class="price__compare">${compare}</s><span class="price__current">${price}</span></span></div>`;
      } else {
        priceEl.innerHTML = `<div class="price"><span class="price__regular"><span class="price__current">${price}</span></span></div>`;
      }
    }

    if (submitBtn) {
      const soldOut = available === false || available === 'false';
      const addLabel = window.theme?.strings?.addToCart || 'Add to Cart';
      const soldLabel = window.theme?.strings?.soldOut || 'Sold Out';
      submitBtn.disabled = soldOut;
      submitBtn.setAttribute('aria-label', soldOut ? soldLabel : addLabel);
      if (!submitBtn.querySelector('.product-card__submit-icon')) {
        submitBtn.textContent = soldOut ? soldLabel : addLabel;
      }
    }

    card.querySelectorAll('[data-product-card-variant]').forEach((btn) => {
      const selected = String(btn.dataset.variantId) === String(id);
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }

  document.addEventListener('click', (e) => {
    const variantBtn = e.target.closest('[data-product-card-variant]');
    if (!variantBtn) return;

    const card = variantBtn.closest('[data-product-card]');
    if (!card) return;

    applyProductCardVariant(card, {
      id: variantBtn.dataset.variantId,
      available: variantBtn.dataset.variantAvailable,
      price: variantBtn.dataset.variantPrice,
      compare: variantBtn.dataset.variantCompare,
    });
  });

  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-product-card-variant-select]');
    if (!select) return;

    const card = select.closest('[data-product-card]');
    const option = select.selectedOptions[0];
    if (!card || !option) return;

    applyProductCardVariant(card, {
      id: option.value,
      available: option.dataset.variantAvailable,
      price: option.dataset.variantPrice,
      compare: option.dataset.variantCompare,
    });
  });

  const ATC_SUCCESS_MIN_MS = 720;

  function setAddToCartSuccess(submitBtn, on) {
    if (!submitBtn) return;
    const isIconSubmit = Boolean(submitBtn.querySelector('.product-card__submit-icon'));
    submitBtn.classList.toggle('is-success', on);
    if (on) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-label', 'Added!');
      if (!isIconSubmit) submitBtn.dataset.atcOriginalText = submitBtn.textContent;
      if (!isIconSubmit) submitBtn.textContent = 'Added!';
    } else {
      submitBtn.disabled = false;
      const originalLabel = submitBtn.dataset.atcOriginalLabel;
      if (originalLabel) submitBtn.setAttribute('aria-label', originalLabel);
      if (!isIconSubmit && submitBtn.dataset.atcOriginalText) {
        submitBtn.textContent = submitBtn.dataset.atcOriginalText;
        delete submitBtn.dataset.atcOriginalText;
      }
    }
  }

  function openProductCardScentDialog(form) {
    const card = form.closest('[data-product-card]');
    const dialog = card?.querySelector('[data-product-card-scent-dialog]');
    const submitBtn = card?.querySelector('[data-product-card-submit]');
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    card.classList.add('is-scent-open');
    if (submitBtn) submitBtn.setAttribute('aria-expanded', 'true');
    dialog.addEventListener(
      'close',
      function () {
        card.classList.remove('is-scent-open');
        if (submitBtn) submitBtn.setAttribute('aria-expanded', 'false');
      },
      { once: true }
    );
    dialog.showModal();
    return true;
  }

  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-scent-dialog-close]');
    if (closeBtn) {
      closeBtn.closest('[data-product-card-scent-dialog]')?.close();
      return;
    }

    const dialog = e.target.closest('[data-product-card-scent-dialog]');
    if (dialog && e.target === dialog) {
      dialog.close();
      return;
    }

    const scentOption = e.target.closest('[data-product-card-scent-option]');
    if (!scentOption || scentOption.disabled) return;

    const card = scentOption.closest('[data-product-card]');
    const form = card?.querySelector('[data-product-card-form]');
    const scentDialog = scentOption.closest('[data-product-card-scent-dialog]');
    if (!card || !form) return;

    applyProductCardVariant(card, {
      id: scentOption.dataset.variantId,
      available: scentOption.dataset.variantAvailable,
      price: scentOption.dataset.variantPrice,
      compare: scentOption.dataset.variantCompare,
    });
    scentDialog?.close();
    form.requestSubmit();
  });

  // Product form add to cart
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;

    e.preventDefault();

    const variantInput = form.querySelector('[data-product-card-variant-id]');
    if (form.dataset.requireChoice === 'true' && !String(variantInput?.value || '').trim()) {
      openProductCardScentDialog(form);
      return;
    }

    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn?.classList.contains('is-success')) return;

    const originalLabel = submitBtn?.getAttribute('aria-label');
    if (submitBtn && originalLabel) submitBtn.dataset.atcOriginalLabel = originalLabel;

    /* Instant feedback masks cart network latency. */
    setAddToCartSuccess(submitBtn, true);
    const startedAt = performance.now();

    try {
      const formData = new FormData(form);
      const response = await fetch(window.theme?.routes?.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      await response.json();
      /* Badge first; drawer can catch up without holding the button state. */
      await refreshCartUI({ openDrawer: true, awaitDrawer: false });

      const elapsed = performance.now() - startedAt;
      if (elapsed < ATC_SUCCESS_MIN_MS) {
        await new Promise((resolve) => setTimeout(resolve, ATC_SUCCESS_MIN_MS - elapsed));
      }
      setAddToCartSuccess(submitBtn, false);
      if (form.dataset.requireChoice === 'true' && variantInput) {
        variantInput.value = '';
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      setAddToCartSuccess(submitBtn, false);
      alert(window.theme?.strings?.cartError || 'There was an error updating your cart');
    }
  });

  function applyCartCount(itemCount) {
    const count = Number(itemCount) || 0;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.classList.toggle('header__cart-count--empty', count === 0);
    });
  }

  // Update cart count in header
  async function updateCartCount(cart) {
    try {
      if (!cart) {
        const response = await fetch(window.theme?.routes?.cart || '/cart.js', {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to fetch cart');
        cart = await response.json();
      }
      applyCartCount(cart.item_count);
      return cart;
    } catch (error) {
      console.error('Error updating cart count:', error);
      return null;
    }
  }

  // Open cart drawer (header button only — never via swipe / history)
  function openCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehaviorX = 'none';
  }

  // Close cart drawer
  function closeCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-active');
    document.body.style.overflow = '';
    document.documentElement.style.overscrollBehaviorX = '';
  }

  /* Ignore overlay "clicks" that were actually swipes (mobile edge gestures). */
  const overlayGesture = { x: 0, y: 0, dragged: false };
  const OVERLAY_DRAG_PX = 12;

  document.addEventListener('pointerdown', (e) => {
    if (!e.target.closest('[data-cart-drawer-overlay]')) return;
    overlayGesture.x = e.clientX;
    overlayGesture.y = e.clientY;
    overlayGesture.dragged = false;
  }, { passive: true });

  document.addEventListener('pointermove', (e) => {
    if (!e.target.closest('[data-cart-drawer-overlay]')) return;
    if (
      Math.abs(e.clientX - overlayGesture.x) > OVERLAY_DRAG_PX
      || Math.abs(e.clientY - overlayGesture.y) > OVERLAY_DRAG_PX
    ) {
      overlayGesture.dragged = true;
    }
  }, { passive: true });

  /**
   * Refresh drawer markup via Section Rendering API.
   * cart-drawer must be a section (not a snippet) for this to work.
   */
  async function refreshCartDrawer() {
    try {
      const url = new URL(window.location.origin + (window.theme?.routes?.root || '/'));
      url.searchParams.set('section_id', 'cart-drawer');
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error('Failed to refresh cart drawer');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-cart-drawer]');
      const currentDrawer = document.querySelector('[data-cart-drawer]');

      if (!newContent || !currentDrawer) return;

      const wasOpen = currentDrawer.classList.contains('is-active');
      currentDrawer.innerHTML = newContent.innerHTML;
      if (wasOpen) currentDrawer.classList.add('is-active');
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  /*
   * Hanger add-on pricing (CRO): matching candle unlocks the $9.99 add-on.
   * - Full-price hanger + matching candle -> swap hanger to $9.99 add-on
   * - $9.99 add-on without matching candle -> swap back to $14.99 hanger
   * One discounted hanger per matching candle, by scent. Flash explains the swap.
   */
  function normalizeScentKey(raw) {
    return String(raw || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function addonScentKey(item) {
    const type = String(item.product_type || '').toLowerCase();
    let raw = '';
    if (type === 'candle') {
      raw = item.handle || item.product_title || '';
    } else if (type === 'add-on') {
      raw = item.variant_title || '';
      if (!raw || raw === 'Default Title') raw = item.product_title || '';
    } else if (type.includes('hanger')) {
      raw = item.handle || '';
      raw = raw.replace(/-hanger$/, '');
      if (!raw) {
        raw = String(item.product_title || '').replace(/\s*hanger\s*$/i, '');
      }
    } else {
      return '';
    }
    return normalizeScentKey(raw);
  }

  function showAddonFlash(kind) {
    const messages = {
      upgrade: 'Matching hanger updated to $9.99 with your candle.',
      downgrade: 'Matching hanger back to $14.99. Add the matching candle for $9.99.',
      removed: 'Matching hanger removed. The matching candle is required.'
    };
    const text = messages[kind];
    if (!text) return;
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const content = drawer.querySelector('.cart-drawer__content');
    if (!content) return;
    content.querySelectorAll('[data-addon-flash]').forEach((el) => el.remove());
    const el = document.createElement('p');
    el.className = 'cart-drawer__addon-flash';
    el.setAttribute('data-addon-flash', '');
    el.setAttribute('role', 'status');
    el.textContent = text;
    const header = content.querySelector('.cart-drawer__header');
    if (header) header.insertAdjacentElement('afterend', el);
    else content.prepend(el);
    window.setTimeout(() => { el.remove(); }, 5000);
  }

  const addonVariantByScent = Object.create(null);
  const hangerVariantByScent = Object.create(null);

  async function ensureAddonVariantMap() {
    if (Object.keys(addonVariantByScent).length) return;
    const res = await fetch('/products/matching-car-hanger-add-on.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const data = await res.json();
    for (const v of data.variants || []) {
      const key = normalizeScentKey(v.title);
      if (key && v.available !== false) addonVariantByScent[key] = v.id;
    }
  }

  async function hangerVariantIdForScent(scent, cartItems) {
    if (hangerVariantByScent[scent]) return hangerVariantByScent[scent];
    for (const item of cartItems || []) {
      const type = String(item.product_type || '').toLowerCase();
      if (type.includes('hanger') && !type.includes('add-on') && addonScentKey(item) === scent) {
        hangerVariantByScent[scent] = item.variant_id;
        return item.variant_id;
      }
    }
    const res = await fetch(`/products/${scent}-hanger.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json();
    const v = (data.variants || []).find((x) => x.available !== false) || (data.variants || [])[0];
    if (!v) return null;
    hangerVariantByScent[scent] = v.id;
    return v.id;
  }

  async function cartChangeLine(key, quantity) {
    const res = await fetch(window.theme?.routes?.cartChange || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!res.ok) return null;
    return res.json();
  }

  async function cartAddLines(items) {
    const res = await fetch(window.theme?.routes?.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!res.ok) return null;
    const cartRes = await fetch(window.theme?.routes?.cart || '/cart.js', { headers: { Accept: 'application/json' } });
    if (!cartRes.ok) return null;
    return cartRes.json();
  }

  async function enforceAddonRule(cart) {
    const empty = { cart, flash: null };
    try {
      if (!cart) {
        const res = await fetch(window.theme?.routes?.cart || '/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return empty;
        cart = await res.json();
      }

      let flash = null;
      let changed = false;
      await ensureAddonVariantMap();

      /* Re-scan after each mutation so keys stay valid. */
      for (let pass = 0; pass < 8; pass += 1) {
        const items = cart.items || [];
        const typeOf = (item) => String(item.product_type || '').toLowerCase();
        const candleQty = Object.create(null);
        const addonsByScent = Object.create(null);
        const hangersByScent = Object.create(null);

        for (const item of items) {
          const type = typeOf(item);
          const scent = addonScentKey(item);
          if (!scent) continue;
          if (type === 'candle') {
            candleQty[scent] = (candleQty[scent] || 0) + item.quantity;
          } else if (type === 'add-on') {
            if (!addonsByScent[scent]) addonsByScent[scent] = [];
            addonsByScent[scent].push(item);
          } else if (type.includes('hanger')) {
            if (!hangersByScent[scent]) hangersByScent[scent] = [];
            hangersByScent[scent].push(item);
          }
        }

        const scents = new Set([
          ...Object.keys(candleQty),
          ...Object.keys(addonsByScent),
          ...Object.keys(hangersByScent)
        ]);

        let did = false;
        for (const scent of scents) {
          const candles = candleQty[scent] || 0;
          const addonLines = addonsByScent[scent] || [];
          const hangerLines = hangersByScent[scent] || [];
          const addonCount = addonLines.reduce((n, i) => n + i.quantity, 0);
          const hangerCount = hangerLines.reduce((n, i) => n + i.quantity, 0);

          /* Upgrade: full-price hangers -> $9.99 add-ons, one per matching candle. */
          const canUpgrade = Math.min(hangerCount, Math.max(0, candles - addonCount));
          if (canUpgrade > 0) {
            const addonVariantId = addonVariantByScent[scent];
            if (!addonVariantId) continue;
            let left = canUpgrade;
            for (const line of hangerLines.slice().reverse()) {
              if (left <= 0) break;
              const cut = Math.min(line.quantity, left);
              const next = await cartChangeLine(line.key, line.quantity - cut);
              if (!next) break;
              cart = next;
              const added = await cartAddLines([{ id: addonVariantId, quantity: cut }]);
              if (!added) break;
              cart = added;
              left -= cut;
              did = true;
              changed = true;
              flash = 'upgrade';
            }
            if (did) break;
          }

          /* Downgrade: excess $9.99 add-ons -> $14.99 hangers when candle count drops. */
          const excess = addonCount - candles;
          if (excess > 0) {
            const hangerVariantId = await hangerVariantIdForScent(scent, items);
            if (!hangerVariantId) {
              /* Fallback: remove excess add-ons if hanger product missing. */
              let left = excess;
              for (const line of addonLines.slice().reverse()) {
                if (left <= 0) break;
                const cut = Math.min(line.quantity, left);
                const next = await cartChangeLine(line.key, line.quantity - cut);
                if (!next) break;
                cart = next;
                left -= cut;
                did = true;
                changed = true;
                flash = flash || 'removed';
              }
              if (did) break;
              continue;
            }
            let left = excess;
            for (const line of addonLines.slice().reverse()) {
              if (left <= 0) break;
              const cut = Math.min(line.quantity, left);
              const next = await cartChangeLine(line.key, line.quantity - cut);
              if (!next) break;
              cart = next;
              const added = await cartAddLines([{ id: hangerVariantId, quantity: cut }]);
              if (!added) break;
              cart = added;
              left -= cut;
              did = true;
              changed = true;
              flash = 'downgrade';
            }
            if (did) break;
          }
        }
        if (!did) break;
      }

      if (changed) {
        document.dispatchEvent(new CustomEvent('kakara:addon-trimmed', { detail: { cart, flash } }));
      }
      return { cart, flash };
    } catch (error) {
      console.error('Error checking hanger add-on pricing:', error);
      return { cart, flash: null };
    }
  }

  async function refreshCartUI({ cart, openDrawer = false, awaitDrawer = true } = {}) {
    const result = await enforceAddonRule(cart);
    cart = result.cart;
    await updateCartCount(cart);
    if (awaitDrawer) {
      await refreshCartDrawer();
    } else {
      refreshCartDrawer();
    }
    if (openDrawer) openCartDrawer();
    if (result.flash) showAddonFlash(result.flash);
  }

  // Update cart item quantity / remove
  async function updateCartItem(key, quantity) {
    try {
      const response = await fetch(window.theme?.routes?.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) throw new Error('Failed to update cart');

      const cart = await response.json();
      await refreshCartUI({ cart });
    } catch (error) {
      console.error('Error updating cart:', error);
      alert(window.theme?.strings?.cartError || 'There was an error updating your cart');
    }
  }

  // Cart drawer interactions (delegated — survives HTML refresh)
  document.addEventListener('click', (e) => {
    const cartToggle = e.target.closest('[data-cart-toggle]');
    if (cartToggle) {
      e.preventDefault();
      openCartDrawer();
      refreshCartDrawer();
      return;
    }

    if (e.target.closest('[data-cart-drawer-close]')) {
      e.preventDefault();
      closeCartDrawer();
      return;
    }

    if (e.target.closest('[data-cart-drawer-overlay]')) {
      /* Desktop / intentional tap only — swipes must not dismiss the cart. */
      if (overlayGesture.dragged) {
        overlayGesture.dragged = false;
        return;
      }
      closeCartDrawer();
      return;
    }

    const addonBtn = e.target.closest('[data-cart-drawer] [data-cart-addon]');
    if (addonBtn) {
      e.preventDefault();
      if (addonBtn.disabled) return;
      const variantId = parseInt(addonBtn.dataset.variantId, 10);
      if (!Number.isFinite(variantId)) return;
      addonBtn.disabled = true;
      addonBtn.setAttribute('aria-busy', 'true');
      fetch(window.theme?.routes?.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then((response) => {
          if (!response.ok) throw new Error('Failed to add add-on');
          return response.json();
        })
        .then(() => refreshCartUI())
        .catch((error) => {
          console.error('Error adding add-on:', error);
          addonBtn.disabled = false;
          addonBtn.removeAttribute('aria-busy');
          alert(window.theme?.strings?.cartError || 'There was an error updating your cart');
        });
      return;
    }

    if (e.target.closest('[data-cart-drawer] [data-cart-offer-link]')) {
      /* Let the #scent-drop anchor navigate, just close the drawer first. */
      closeCartDrawer();
      return;
    }

    const qtyBtn = e.target.closest('[data-cart-drawer] [data-quantity-change]');
    if (qtyBtn) {
      e.preventDefault();
      const key = qtyBtn.dataset.key;
      const quantity = parseInt(qtyBtn.dataset.quantity, 10);
      if (key && Number.isFinite(quantity)) updateCartItem(key, Math.max(0, quantity));
      return;
    }

    const removeBtn = e.target.closest('[data-cart-drawer] [data-remove-item]');
    if (removeBtn) {
      e.preventDefault();
      const key = removeBtn.dataset.key;
      if (key) updateCartItem(key, 0);
    }
  });

  document.addEventListener('change', (e) => {
    const note = e.target.closest('[data-cart-drawer] [data-cart-note]');
    if (!note) return;

    fetch(window.theme?.routes?.cartUpdate || '/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ note: note.value })
    }).catch((error) => {
      console.error('Error updating cart note:', error);
    });
  });

  // Expose for cart page / other scripts
  window.KakaraCart = {
    updateCount: updateCartCount,
    refreshDrawer: refreshCartDrawer,
    refreshUI: refreshCartUI,
    openDrawer: openCartDrawer,
    closeDrawer: closeCartDrawer,
    enforceAddonRule
  };

  /* Also check on page load (cart page, back button, other tabs). */
  let addonCheckedOnLoad = false;
  document.addEventListener('kakara:addon-trimmed', (e) => {
    if (addonCheckedOnLoad) return;
    if (/\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }
    updateCartCount(e.detail?.cart);
    refreshCartDrawer().then(() => {
      if (e.detail?.flash) showAddonFlash(e.detail.flash);
    });
  });
  enforceAddonRule().finally(() => { addonCheckedOnLoad = true; });

  // Share buttons - copy URL
  document.addEventListener('click', (e) => {
    const copyBtn = e.target.closest('[data-copy-url]');
    if (copyBtn) {
      const url = copyBtn.dataset.copyUrl;
      navigator.clipboard.writeText(url).then(() => {
        const originalContent = copyBtn.innerHTML;
        copyBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="20 6 9 17 4 12"></polyline></svg>';
        setTimeout(() => {
          copyBtn.innerHTML = originalContent;
        }, 2000);
      });
    }
  });

  // Collection sorting
  document.addEventListener('change', (e) => {
    const sortSelect = e.target.closest('[data-sort-select]');
    if (sortSelect) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    }
  });

  // Login form toggle
  document.addEventListener('click', (e) => {
    const showRecover = e.target.closest('[data-show-recover]');
    const showLogin = e.target.closest('[data-show-login]');

    if (showRecover) {
      e.preventDefault();
      document.getElementById('CustomerLogin')?.style.setProperty('display', 'none');
      document.getElementById('RecoverPassword')?.style.setProperty('display', 'block');
    }

    if (showLogin) {
      e.preventDefault();
      document.getElementById('CustomerLogin')?.style.setProperty('display', 'block');
      document.getElementById('RecoverPassword')?.style.setProperty('display', 'none');
    }
  });

  // Address form toggle
  document.addEventListener('click', (e) => {
    const toggleBtn = e.target.closest('[data-address-toggle]');
    const cancelBtn = e.target.closest('[data-address-cancel]');
    const formWrapper = document.querySelector('[data-address-form]');

    if (toggleBtn && formWrapper) {
      formWrapper.style.display = formWrapper.style.display === 'none' ? 'block' : 'none';
    }

    if (cancelBtn && formWrapper) {
      formWrapper.style.display = 'none';
    }
  });

  // Address delete confirmation
  document.addEventListener('click', (e) => {
    const deleteBtn = e.target.closest('[data-confirm]');
    if (deleteBtn) {
      const message = deleteBtn.dataset.confirm;
      if (!confirm(message)) {
        e.preventDefault();
      }
    }
  });
})();
